import { escapeHtml } from './escapeHtml';
import { extractGstFromInclusive } from './currency';
import { tradeGstCents } from '../core/financial';
import { payoutAmountIsExGst } from '../core/payoutAmount';
import { PLATFORM_ENTITY } from '../constants/platformEntity';

export interface RemittanceAdviceData {
  raNumber: string;
  date: string;
  jobId: string;
  jobTitle: string;
  transactionType: string;
  amountCents: number;
  gstAmountCents?: number | null;
  stripeTransferId?: string | null;
  tradeCompanyName: string;
  tradeCompanyAbn?: string | null;
  /**
   * The job's client company: the payer (card ac4bc6da). Trade Connector only processed the
   * payment on its behalf. When unknown the advice says "the client company for this job".
   */
  clientCompanyName?: string | null;
  clientCompanyAbn?: string | null;
  status?: string;
  /** The payout row's `metadata.amount_convention` ('GROSS' = amount is GST-inclusive for every type). */
  amountConvention?: string | null;
  /** Derives the GST when the row recorded none (registration as of `asOf`). */
  tradeCompany?: {
    gst_registered?: boolean | null;
    gst_effective_from?: string | Date | null;
  } | null;
  /** The payout date the GST registration is judged at (defaults to `date`, else now). */
  asOf?: Date | string | null;
  /**
   * Plain-text notes shown with the payment, e.g. why a final release is smaller than the
   * holdback ("Holdback $500.92 − refund to client $200.00 (…) = $300.92").
   */
  notes?: string[];
  bankArrivalDate?: string | null;
  bankDepositAmountCents?: number | null;
  stripePayoutId?: string | null;
}

export interface ConsolidatedRemittanceItem {
  jobId: string;
  jobTitle: string;
  transactionType: string;
  amountCents: number;
  gstAmountCents?: number | null;
  /** The payout row's `metadata.amount_convention` ('GROSS' = amount is GST-inclusive for every type). */
  amountConvention?: string | null;
  stripeTransferId?: string | null;
  tradeCompany?: {
    gst_registered?: boolean | null;
    gst_effective_from?: string | Date | null;
  } | null;
  createdAt?: string;
  status?: string;
  asOf?: Date;
  /** The job's client company, the payer of this line (see RemittanceAdviceData.clientCompanyName). */
  clientCompanyName?: string | null;
  clientCompanyAbn?: string | null;
  /** Plain-text notes shown under the line (see RemittanceAdviceData.notes). */
  notes?: string[];
}

/** The non-empty notes, trimmed. */
function cleanNotes(notes: string[] | null | undefined): string[] {
  return (notes ?? []).map((n) => (n ?? '').trim()).filter(Boolean);
}

/** Notes as small grey lines under a description cell (escaped). */
function notesCellHtml(notes: string[] | null | undefined): string {
  return cleanNotes(notes)
    .map((n) => `<div class="line-note" style="font-size: 12px; color: #92400e; margin-top: 4px;">${escapeHtml(n)}</div>`)
    .join('');
}

export interface ConsolidatedRemittanceAdviceData {
  payoutId: string;
  raNumber: string;
  payoutDate: string;
  tradeCompanyName: string;
  tradeCompanyAbn?: string | null;
  /** The payer is each item's client company (one payer when they all share it). */
  items: ConsolidatedRemittanceItem[];
  totalAmountCents?: number;
  status?: string;
  /** Default for items without their own `tradeCompany`. */
  tradeCompany?: {
    gst_registered?: boolean | null;
    gst_effective_from?: string | Date | null;
  } | null;
  /**
   * 'batch' (default): every payment in one bank deposit (`payoutId` is the bank payout).
   * 'job': every payment made on ONE job, in date order, with the job's total: the trade's
   * "Payout Summary & Remittance Advice" (initial, final and every variation payout). `payoutId`
   * is not shown; `payoutDate` is the date the summary is issued.
   */
  scope?: 'batch' | 'job';
  /** scope 'job': the job (defaults to the first item's). */
  jobId?: string | null;
  jobTitle?: string | null;
}

export interface RemittanceFinancials {
  netCents: number;
  gstCents: number;
  totalCents: number;
  netFormatted: string;
  gstFormatted: string;
  totalFormatted: string;
}

/**
 * Calculates net, GST, and gross totals in cents and dollar strings for a payout transaction.
 *
 * The gross total follows the payout-row convention (`payoutAmountIsExGst`, core/payoutAmount):
 * a row stamped `amountConvention = 'GROSS'` already holds the gross in `amountCents`; an
 * unstamped MILESTONE_RELEASE / VARIATION_RELEASE / CLAWBACK row holds the ex-GST amount and its
 * gross is amount + GST; every other type holds the gross.
 *
 * GST: a stored `gstAmountCents` wins (#3419: it is what the payout recorded). Without one, it is
 * derived from the trade's registration as of `asOf` when `tradeCompany` is given, else 1/11 of
 * a gross amount (0 for an ex-GST amount, whose GST was never recorded).
 */
export function calculateRemittanceFinancials(
  amountCents: number,
  gstAmountCents: number | null | undefined,
  transactionType: string,
  tradeCompany?: {
    gst_registered?: boolean | null;
    gst_effective_from?: string | Date | null;
  } | null,
  asOf = new Date(),
  amountConvention?: string | null
): RemittanceFinancials {
  const isExGst = payoutAmountIsExGst({
    transaction_type: transactionType,
    amount_convention: amountConvention ?? null,
  });

  let gstCents: number;
  let totalCents: number;
  let netCents: number;

  if (gstAmountCents != null) {
    gstCents = gstAmountCents;
    totalCents = isExGst ? amountCents + gstCents : amountCents;
    netCents = totalCents - gstCents;
  } else if (tradeCompany !== undefined) {
    const isRegistered = Boolean(
      tradeCompany?.gst_registered &&
        (tradeCompany.gst_effective_from == null || new Date(tradeCompany.gst_effective_from) <= asOf)
    );
    if (!isRegistered) {
      gstCents = 0;
      totalCents = amountCents;
      netCents = amountCents;
    } else if (isExGst) {
      netCents = amountCents;
      gstCents = tradeGstCents(netCents, tradeCompany, asOf);
      totalCents = netCents + gstCents;
    } else {
      totalCents = amountCents;
      gstCents = tradeGstCents(Math.round(totalCents / 1.1), tradeCompany, asOf);
      netCents = totalCents - gstCents;
    }
  } else {
    totalCents = amountCents;
    gstCents = isExGst ? 0 : extractGstFromInclusive(amountCents).gst;
    netCents = totalCents - gstCents;
  }

  return {
    netCents,
    gstCents,
    totalCents,
    netFormatted: (netCents / 100).toFixed(2),
    gstFormatted: (gstCents / 100).toFixed(2),
    totalFormatted: (totalCents / 100).toFixed(2),
  };
}

/** A usable date for the GST registration check: the first parseable candidate, else now. */
function remittanceAsOf(...candidates: Array<Date | string | null | undefined>): Date {
  for (const c of candidates) {
    if (c == null || c === '') continue;
    const d = c instanceof Date ? c : new Date(c);
    if (!isNaN(d.getTime())) return d;
  }
  return new Date();
}

/**
 * Generates standard Remittance Advice identifier e.g. "RA-20260824-AC698E34"
 */
export function formatRemittanceAdviceNumber(createdAt: string, jobId: string): string {
  const dateStr = (createdAt || new Date().toISOString()).slice(0, 10).replace(/-/g, '');
  const jobSnippet = (jobId || '').replace(/-/g, '').substring(0, 8).toUpperCase();
  return `RA-${dateStr}-${jobSnippet}`;
}

/**
 * Formats machine transaction types into user-facing labels. Card ac4bc6da: the trade is never
 * shown the payout split, so the staged releases say only which payment they are.
 */
export function formatTransactionTypeLabel(transactionType: string): string {
  switch (transactionType) {
    case 'INITIAL_RELEASE':
      return 'Initial Payout';
    case 'ON_SITE_INITIAL_RELEASE':
      return 'Initial Payout (on-site)';
    case 'FINAL_RELEASE':
    case 'RETENTION_RELEASE':
      return 'Final Payout';
    case 'MILESTONE_RELEASE':
      return 'Milestone Release';
    case 'VARIATION_RELEASE':
      return 'Variation Payout';
    case 'CANCELLATION_COMPENSATION':
      return 'Cancellation Compensation';
    case 'CLAWBACK':
      return 'Clawback / Adjustment';
    // REFUND_REVERSAL_TRANSACTION_TYPE (core/payoutReversal): what a client refund took back from
    // the trade's transfers; never the client's refund itself.
    case 'REFUND_REVERSAL':
      return 'Reversed: client refund';
    case 'SETTLEMENT_RELEASE':
      return 'Settlement Release';
    default:
      return transactionType.replace(/_/g, ' ');
  }
}

/** A party block on a remittance advice (the payer or the payee). */
export interface RemittanceAdviceParty {
  heading: string;
  name: string;
  lines: string[];
}

/** One payment a remittance advice confirms. Amounts are formatted dollars, e.g. "$2.24". */
export interface RemittanceAdvicePayment {
  jobTitle: string;
  jobId: string;
  jobRef: string;
  paymentType: string;
  /** The client company the payment was made on behalf of; null when it is not known. */
  clientCompanyName: string | null;
  /**
   * "On behalf of <client company>" against this payment, when a batch names its clients per
   * payment (the payer reads "the client companies shown against each payment"); otherwise null.
   */
  onBehalfOf: string | null;
  transferId: string | null;
  net: string;
  gst: string;
  total: string;
  notes: string[];
  /** scope 'job' only: the payment's date, e.g. "9 Oct 2026" (Australia/Sydney). */
  date?: string;
}

/**
 * Everything a remittance advice says, so the HTML page and the PDF say the same thing.
 *
 * Card ac4bc6da (finance): the client company is the payer and Trade Connector only processed the
 * payment on its behalf; the document confirms a payment, it is not an invoice to the trade; and
 * the trade never sees the payout split. Trade Connector appears only as the issuer.
 */
export interface RemittanceAdviceContent {
  kind: 'single' | 'batch' | 'job';
  /** kind 'job' only: the job every payment belongs to. */
  job?: { id: string; ref: string; title: string };
  title: string;
  raNumber: string;
  meta: Array<{ label: string; value: string }>;
  /** "Paid on behalf of <client company> via Trade Connector". */
  onBehalfOf: string;
  payer: RemittanceAdviceParty;
  payee: RemittanceAdviceParty;
  payments: RemittanceAdvicePayment[];
  totals: { label: string; net: string; gst: string; total: string };
  references: string[];
  footer: string;
  issuer: string;
}

const SINGLE_CLIENT_FALLBACK = 'the client company for this job';
const BATCH_CLIENT_FALLBACK = 'the client companies for these jobs';
const BATCH_CLIENT_PER_ROW = 'the client companies shown against each payment';

function cleanText(value: string | null | undefined): string | null {
  const trimmed = (value ?? '').trim();
  return trimmed === '' ? null : trimmed;
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function dollars(formatted: string): string {
  return `$${formatted}`;
}

function jobRefOf(jobId: string | null | undefined): string {
  return (jobId || '').replace(/-/g, '').substring(0, 8).toUpperCase();
}

/** "Paid" for a completed payout; otherwise the status in sentence case. */
function paymentStatusLabel(status: string | null | undefined): string {
  const s = (status || 'PAID').trim().toUpperCase();
  if (s === 'PAID' || s === 'COMPLETED') return 'Paid';
  return capitalise(s.toLowerCase().replace(/_/g, ' '));
}

function issuerLine(): string {
  return `Issued by ${PLATFORM_ENTITY.name} (ABN ${PLATFORM_ENTITY.abn}) · ${PLATFORM_ENTITY.supportEmail}`;
}

function payeeParty(name: string | null | undefined, abn: string | null | undefined): RemittanceAdviceParty {
  return {
    heading: 'Paid to',
    name: cleanText(name) ?? 'Trade Company',
    lines: [`ABN: ${cleanText(abn) ?? 'Not provided'}`],
  };
}

/** The content of a single-payment remittance advice. */
export function buildRemittanceAdviceContent(data: RemittanceAdviceData): RemittanceAdviceContent {
  const financials = calculateRemittanceFinancials(
    data.amountCents,
    data.gstAmountCents,
    data.transactionType,
    data.tradeCompany,
    remittanceAsOf(data.asOf, data.date),
    data.amountConvention
  );

  const client = cleanText(data.clientCompanyName);
  const clientAbn = cleanText(data.clientCompanyAbn);
  const payee = payeeParty(data.tradeCompanyName, data.tradeCompanyAbn);
  const jobRef = jobRefOf(data.jobId);
  const transferId = cleanText(data.stripeTransferId);

  const bankDepositParts: string[] = [];
  if (data.bankArrivalDate) bankDepositParts.push(`Arrival: ${data.bankArrivalDate}`);
  if (data.bankDepositAmountCents != null) {
    bankDepositParts.push(`Deposit amount: $${(data.bankDepositAmountCents / 100).toFixed(2)}`);
  }
  if (data.stripePayoutId) bankDepositParts.push(`Payout ref: ${data.stripePayoutId}`);

  const references: string[] = [];
  if (bankDepositParts.length > 0) references.push(`Bank deposit: ${bankDepositParts.join(' · ')}`);
  if (transferId) references.push(`Payment reference (Stripe transfer): ${transferId}`);
  references.push(`Status: ${paymentStatusLabel(data.status)}`);

  return {
    kind: 'single',
    title: 'Remittance Advice',
    raNumber: data.raNumber,
    meta: [
      { label: 'Payment date', value: data.date },
      { label: 'Job reference', value: jobRef },
    ],
    onBehalfOf: `Paid on behalf of ${client ?? SINGLE_CLIENT_FALLBACK} via Trade Connector`,
    payer: {
      heading: 'Payer',
      name: client ?? capitalise(SINGLE_CLIENT_FALLBACK),
      lines: clientAbn ? [`ABN: ${clientAbn}`] : [],
    },
    payee,
    payments: [{
      jobTitle: cleanText(data.jobTitle) ?? 'Job Services',
      jobId: data.jobId,
      jobRef,
      paymentType: formatTransactionTypeLabel(data.transactionType),
      clientCompanyName: client,
      onBehalfOf: null,
      transferId,
      net: dollars(financials.netFormatted),
      gst: dollars(financials.gstFormatted),
      total: dollars(financials.totalFormatted),
      notes: cleanNotes(data.notes),
    }],
    totals: {
      label: 'Amount paid',
      net: dollars(financials.netFormatted),
      gst: dollars(financials.gstFormatted),
      total: dollars(financials.totalFormatted),
    },
    references,
    footer: `This remittance advice confirms a payment made to ${payee.name} on behalf of ${client ?? SINGLE_CLIENT_FALLBACK} via Trade Connector. It is not a tax invoice.`,
    issuer: issuerLine(),
  };
}

/** A payment date for the per-job summary, e.g. "9 Oct 2026", as an Australian trade reads it. */
function paymentDateLabel(createdAt: string | null | undefined): string {
  if (!createdAt) return '';
  const d = new Date(createdAt);
  if (isNaN(d.getTime())) return createdAt;
  try {
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Australia/Sydney' });
  } catch {
    return createdAt.slice(0, 10);
  }
}

/** Items in payment-date order; an item without a usable date keeps its place after the dated ones. */
function inDateOrder(items: ConsolidatedRemittanceItem[]): ConsolidatedRemittanceItem[] {
  const time = (item: ConsolidatedRemittanceItem) => {
    const t = item.createdAt ? Date.parse(item.createdAt) : NaN;
    return isNaN(t) ? Number.POSITIVE_INFINITY : t;
  };
  return items
    .map((item, index) => ({ item, index, t: time(item) }))
    .sort((a, b) => (a.t === b.t ? a.index - b.index : a.t < b.t ? -1 : 1))
    .map(({ item }) => item);
}

/**
 * The content of a consolidated remittance advice: every payment in one bank deposit (scope
 * 'batch', the default) or every payment made on one job (scope 'job', the trade's payout summary:
 * initial, final and each variation payout, in date order, with the job's total).
 */
export function buildConsolidatedRemittanceAdviceContent(
  data: ConsolidatedRemittanceAdviceData
): RemittanceAdviceContent {
  let grandNetCents = 0;
  let grandGstCents = 0;
  let grandTotalCents = 0;

  const isJob = data.scope === 'job';
  const items = isJob ? inDateOrder(data.items || []) : data.items || [];
  const clientNames = items.map((item) => cleanText(item.clientCompanyName));
  const distinctClients = Array.from(new Set(clientNames.filter((n): n is string => n != null)));
  // One payer only when every payment names the same client company.
  const singleClient = distinctClients.length === 1 && clientNames.every((n) => n != null)
    ? distinctClients[0]
    : null;
  const singleClientAbn = singleClient
    ? items.map((item) => cleanText(item.clientCompanyAbn)).find((abn) => abn != null) ?? null
    : null;
  // One job has one client company: when it is not known the summary says "for this job".
  const clientPhrase = isJob
    ? singleClient ?? distinctClients[0] ?? SINGLE_CLIENT_FALLBACK
    : singleClient ?? (distinctClients.length > 0 ? BATCH_CLIENT_PER_ROW : BATCH_CLIENT_FALLBACK);
  const clientPerRow = clientPhrase === BATCH_CLIENT_PER_ROW;

  const payments: RemittanceAdvicePayment[] = items.map((item, index) => {
    const financials = calculateRemittanceFinancials(
      item.amountCents,
      item.gstAmountCents,
      item.transactionType,
      item.tradeCompany ?? data.tradeCompany,
      remittanceAsOf(item.asOf, item.createdAt, data.payoutDate),
      item.amountConvention
    );
    grandNetCents += financials.netCents;
    grandGstCents += financials.gstCents;
    grandTotalCents += financials.totalCents;

    return {
      jobTitle: cleanText(item.jobTitle) ?? 'Job Services',
      jobId: item.jobId,
      jobRef: jobRefOf(item.jobId),
      paymentType: formatTransactionTypeLabel(item.transactionType),
      clientCompanyName: clientNames[index],
      // A payment whose client is not known still answers "shown against each payment".
      onBehalfOf: clientPerRow ? `On behalf of ${clientNames[index] ?? SINGLE_CLIENT_FALLBACK}` : null,
      transferId: cleanText(item.stripeTransferId),
      net: dollars(financials.netFormatted),
      gst: dollars(financials.gstFormatted),
      total: dollars(financials.totalFormatted),
      notes: cleanNotes(item.notes),
      ...(isJob ? { date: paymentDateLabel(item.createdAt) } : {}),
    };
  });

  const payee = payeeParty(data.tradeCompanyName, data.tradeCompanyAbn);
  const jobClientKnown = isJob && clientPhrase !== SINGLE_CLIENT_FALLBACK;
  const payerAbn = jobClientKnown
    ? items.map((item) => cleanText(item.clientCompanyAbn)).find((abn) => abn != null) ?? null
    : singleClientAbn;
  const payer = {
    heading: 'Payer',
    name: jobClientKnown ? clientPhrase : singleClient ?? capitalise(clientPhrase),
    lines: payerAbn ? [`ABN: ${payerAbn}`] : [],
  };
  const totals = {
    label: isJob ? 'Total paid on this job' : 'Total paid',
    net: dollars((grandNetCents / 100).toFixed(2)),
    gst: dollars((grandGstCents / 100).toFixed(2)),
    total: dollars((grandTotalCents / 100).toFixed(2)),
  };
  const footer = `This remittance advice confirms the payments listed above, made to ${payee.name} on behalf of ${clientPhrase} via Trade Connector. It is not a tax invoice.`;

  if (isJob) {
    const jobId = cleanText(data.jobId) ?? items[0]?.jobId ?? '';
    const jobRef = jobRefOf(jobId);
    return {
      kind: 'job',
      job: { id: jobId, ref: jobRef, title: cleanText(data.jobTitle) ?? payments[0]?.jobTitle ?? 'Job Services' },
      title: 'Payout Summary & Remittance Advice',
      raNumber: data.raNumber,
      meta: [
        { label: 'Job reference', value: jobRef },
        { label: 'Date', value: data.payoutDate },
        { label: 'Payments', value: String(items.length) },
      ],
      onBehalfOf: `Paid on behalf of ${clientPhrase} via Trade Connector`,
      payer,
      payee,
      payments,
      totals,
      references: [`Transferred via Stripe Connect · Status: ${paymentStatusLabel(data.status)}`],
      footer,
      issuer: issuerLine(),
    };
  }

  const payoutRef = cleanText(data.payoutId) ?? 'Bank Deposit';

  return {
    kind: 'batch',
    title: 'Remittance Advice (Batch)',
    raNumber: data.raNumber,
    meta: [
      { label: 'Deposit date', value: data.payoutDate },
      { label: 'Bank payout ref', value: payoutRef },
      { label: 'Payments', value: String(items.length) },
    ],
    onBehalfOf: `Paid on behalf of ${clientPhrase} via Trade Connector`,
    payer,
    payee,
    payments,
    totals,
    references: [
      `Bank payout reference: ${payoutRef} · Transferred via Stripe Connect · Status: ${paymentStatusLabel(data.status)}`,
    ],
    footer,
    issuer: issuerLine(),
  };
}

const REMITTANCE_ADVICE_STYLES = `
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
      margin: 40px;
      color: #1f2937;
      background-color: #ffffff;
      line-height: 1.5;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      border-bottom: 3px solid #00875a;
      padding-bottom: 20px;
      margin-bottom: 20px;
    }
    .header h1 {
      color: #00875a;
      margin: 0;
      font-size: 26px;
      font-weight: 700;
      letter-spacing: -0.5px;
    }
    .header .ra-number {
      font-size: 15px;
      color: #4b5563;
      margin-top: 4px;
      font-weight: 600;
    }
    .meta-box {
      text-align: right;
      font-size: 14px;
      color: #374151;
    }
    .on-behalf-of {
      font-size: 15px;
      color: #111827;
      margin-bottom: 24px;
    }
    .company-details {
      display: flex;
      justify-content: space-between;
      margin-bottom: 32px;
      gap: 24px;
    }
    .company-box {
      width: 48%;
      background: #f9fafb;
      padding: 16px;
      border-radius: 8px;
      border: 1px solid #e5e7eb;
    }
    .company-box h3, h2.section-title {
      font-size: 12px;
      text-transform: uppercase;
      color: #6b7280;
      margin-top: 0;
      margin-bottom: 8px;
      letter-spacing: 1px;
    }
    .company-box p {
      margin: 4px 0;
      font-size: 14px;
      color: #1f2937;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 8px 0 24px;
    }
    th {
      background: #f3f4f6;
      padding: 12px;
      text-align: left;
      font-size: 12px;
      text-transform: uppercase;
      color: #4b5563;
      border-bottom: 2px solid #d1d5db;
      font-weight: 600;
    }
    td {
      padding: 12px;
      border-bottom: 1px solid #e5e7eb;
      font-size: 14px;
      color: #1f2937;
    }
    table.details th {
      width: 34%;
      background: transparent;
      border-bottom: 1px solid #e5e7eb;
      font-size: 13px;
      text-transform: none;
      color: #4b5563;
    }
    td.right, th.right {
      text-align: right;
    }
    tr.total th, tr.total td {
      font-weight: 700;
      font-size: 16px;
      border-top: 2px solid #111827;
      color: #111827;
    }
    .sub {
      font-size: 12px;
      color: #6b7280;
      margin-top: 2px;
    }
    .reference-box {
      margin-top: 24px;
      padding: 12px 16px;
      background: #f0fdf4;
      border: 1px solid #bbf7d0;
      border-radius: 6px;
      font-size: 13px;
      color: #166534;
    }
    .footer {
      margin-top: 48px;
      padding-top: 20px;
      border-top: 1px solid #e5e7eb;
      font-size: 12px;
      color: #6b7280;
      text-align: center;
    }
    @media print {
      body { margin: 20px; }
      .no-print { display: none; }
      .company-box { background: transparent !important; }
    }`;

function partyHtml(party: RemittanceAdviceParty): string {
  return `<div class="company-box">
      <h3>${escapeHtml(party.heading)}</h3>
      <p><strong>${escapeHtml(party.name)}</strong></p>
      ${party.lines.map((line) => `<p>${escapeHtml(line)}</p>`).join('\n      ')}
    </div>`;
}

function remittanceAdviceHtml(content: RemittanceAdviceContent, body: string): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapeHtml(content.title)} ${escapeHtml(content.raNumber)}</title>
  <style>${REMITTANCE_ADVICE_STYLES}
  </style>
</head>
<body>
  <div class="header">
    <div>
      <h1>${escapeHtml(content.title)}</h1>
      <div class="ra-number">${escapeHtml(content.raNumber)}</div>
    </div>
    <div class="meta-box">
      ${content.meta.map((m) => `<div><strong>${escapeHtml(m.label)}:</strong> ${escapeHtml(m.value)}</div>`).join('\n      ')}
    </div>
  </div>

  <div class="on-behalf-of">${escapeHtml(content.onBehalfOf)}</div>

  <div class="company-details">
    ${partyHtml(content.payer)}
    ${partyHtml(content.payee)}
  </div>

  ${body}

  <div class="reference-box">
    ${content.references.map((r) => `<div>${escapeHtml(r)}</div>`).join('\n    ')}
  </div>

  <div class="footer">
    <div>${escapeHtml(content.footer)}</div>
    <div style="margin-top: 6px;">${escapeHtml(content.issuer)}</div>
  </div>
</body>
</html>`;
}

/**
 * Generates a print-friendly HTML Remittance Advice confirming one payment to a trade, made on
 * behalf of the job's client company via Trade Connector (card ac4bc6da). Not an invoice.
 */
export function generateRemittanceAdviceHtml(data: RemittanceAdviceData): string {
  const content = buildRemittanceAdviceContent(data);
  const payment = content.payments[0];
  const notes = payment.notes;

  const body = `<h2 class="section-title">Payment details</h2>
  <table class="details">
    <tr>
      <th>Job</th>
      <td>
        <strong>${escapeHtml(payment.jobTitle)}</strong>
        <div class="sub">Job ID: ${escapeHtml(payment.jobId)}</div>
      </td>
    </tr>
    <tr><th>Payment</th><td>${escapeHtml(payment.paymentType)}</td></tr>
    <tr><th>Amount (ex GST)</th><td>${escapeHtml(payment.net)}</td></tr>
    <tr><th>GST</th><td>${escapeHtml(payment.gst)}</td></tr>
    <tr class="total"><th>${escapeHtml(content.totals.label)}</th><td>${escapeHtml(payment.total)}</td></tr>
  </table>

  ${notes.length > 0 ? `<div class="notes-box" style="margin-top: 24px; padding: 12px 16px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; font-size: 13px; color: #92400e;">
    <strong>Notes:</strong>
    ${notes.map((n) => `<div style="margin-top: 4px;">${escapeHtml(n)}</div>`).join('')}
  </div>` : ''}`;

  return remittanceAdviceHtml(content, body);
}

/**
 * Generates standard Consolidated Remittance Advice identifier e.g. "RA-BATCH-20260824-AC698E34"
 */
export function formatConsolidatedRemittanceAdviceNumber(createdAt: string, payoutId: string): string {
  const dateStr = (createdAt || new Date().toISOString()).slice(0, 10).replace(/-/g, '');
  const payoutSnippet = (payoutId || '').replace(/[^a-zA-Z0-9]/g, '').slice(-8).toUpperCase();
  return `RA-BATCH-${dateStr}-${payoutSnippet || 'PAYOUT'}`;
}

/**
 * Generates a print-friendly HTML Remittance Advice for every payment in one bank deposit (see
 * generateRemittanceAdviceHtml). Each line names its client company when they differ.
 */
export function generateConsolidatedRemittanceAdviceHtml(data: ConsolidatedRemittanceAdviceData): string {
  const content = buildConsolidatedRemittanceAdviceContent(data);
  if (content.kind === 'job') return remittanceAdviceHtml(content, jobPaymentsBodyHtml(content));

  const rowsHtml = content.payments.map((p) => `<tr>
      <td>
        <strong>${escapeHtml(p.jobTitle)}</strong>
        <div class="sub"><strong>Ref:</strong> ${escapeHtml(p.jobRef)}${p.transferId ? ` <span style="margin-left: 6px;">(Transfer: <code>${escapeHtml(p.transferId)}</code>)</span>` : ''}</div>
        ${p.onBehalfOf ? `<div class="sub">${escapeHtml(p.onBehalfOf)}</div>` : ''}
        ${notesCellHtml(p.notes)}
      </td>
      <td>${escapeHtml(p.paymentType)}</td>
      <td class="right">${escapeHtml(p.net)}</td>
      <td class="right">${escapeHtml(p.gst)}</td>
      <td class="right" style="font-weight: 600;">${escapeHtml(p.total)}</td>
    </tr>`).join('\n');

  const body = `<h2 class="section-title">Payments</h2>
  <table>
    <thead>
      <tr>
        <th>Job</th>
        <th>Payment</th>
        <th class="right">Amount (ex GST)</th>
        <th class="right">GST</th>
        <th class="right">Amount paid</th>
      </tr>
    </thead>
    <tbody>
      ${rowsHtml || '<tr><td colspan="5" style="text-align:center;">No payments found in this bank deposit</td></tr>'}
      <tr class="total">
        <td colspan="2">${escapeHtml(content.totals.label)}</td>
        <td class="right">${escapeHtml(content.totals.net)}</td>
        <td class="right">${escapeHtml(content.totals.gst)}</td>
        <td class="right">${escapeHtml(content.totals.total)}</td>
      </tr>
    </tbody>
  </table>`;

  return remittanceAdviceHtml(content, body);
}

/** The body of a per-job payout summary (scope 'job'): the job, then each payment in date order. */
function jobPaymentsBodyHtml(content: RemittanceAdviceContent): string {
  const job = content.job ?? { id: '', ref: '', title: 'Job Services' };
  const rowsHtml = content.payments.map((p) => `<tr>
      <td>${escapeHtml(p.date ?? '')}</td>
      <td>
        ${escapeHtml(p.paymentType)}
        ${p.transferId ? `<div class="sub">Transfer: <code>${escapeHtml(p.transferId)}</code></div>` : ''}
        ${notesCellHtml(p.notes)}
      </td>
      <td class="right">${escapeHtml(p.net)}</td>
      <td class="right">${escapeHtml(p.gst)}</td>
      <td class="right" style="font-weight: 600;">${escapeHtml(p.total)}</td>
    </tr>`).join('\n');

  return `<h2 class="section-title">Job</h2>
  <table class="details">
    <tr>
      <th>Job</th>
      <td>
        <strong>${escapeHtml(job.title)}</strong>
        <div class="sub">Job ID: ${escapeHtml(job.id)}</div>
      </td>
    </tr>
  </table>

  <h2 class="section-title">Payments</h2>
  <table>
    <thead>
      <tr>
        <th>Date</th>
        <th>Payment</th>
        <th class="right">Amount (ex GST)</th>
        <th class="right">GST</th>
        <th class="right">Amount paid</th>
      </tr>
    </thead>
    <tbody>
      ${rowsHtml || '<tr><td colspan="5" style="text-align:center;">No payments have been made on this job yet</td></tr>'}
      <tr class="total">
        <td colspan="2">${escapeHtml(content.totals.label)}</td>
        <td class="right">${escapeHtml(content.totals.net)}</td>
        <td class="right">${escapeHtml(content.totals.gst)}</td>
        <td class="right">${escapeHtml(content.totals.total)}</td>
      </tr>
    </tbody>
  </table>`;
}

/**
 * Generates an inline-styled HTML table for inclusion in transactional payout notification emails
 */
export function generateRemittanceEmailTableHtml(items: ConsolidatedRemittanceItem[], unmatchedCents?: number): string {
  const hasItems = items && items.length > 0;
  const hasUnmatched = typeof unmatchedCents === 'number' && unmatchedCents !== 0;
  if (!hasItems && !hasUnmatched) {
    return '<p style="color: #6b7280; font-style: italic;">No itemized breakdown available for this payout.</p>';
  }

  let totalNetCents = 0;
  let totalGstCents = 0;
  let totalCentsSum = 0;

  const rows = (items || []).map((item) => {
    const fin = calculateRemittanceFinancials(item.amountCents, item.gstAmountCents, item.transactionType, item.tradeCompany, item.asOf, item.amountConvention);
    totalNetCents += fin.netCents;
    totalGstCents += fin.gstCents;
    totalCentsSum += fin.totalCents;

    const label = formatTransactionTypeLabel(item.transactionType);
    const jobSnippet = item.jobId ? (item.jobId.replace(/-/g, '').substring(0, 8).toUpperCase()) : '';

    return `<tr style="border-bottom: 1px solid #e5e7eb;">
      <td style="padding: 10px 12px; font-size: 14px; color: #1f2937;">
        <strong>${escapeHtml(item.jobTitle || 'Job Services')}</strong>
        ${jobSnippet ? `<div style="font-size: 12px; color: #6b7280;">Ref: ${escapeHtml(jobSnippet)}</div>` : ''}
        ${notesCellHtml(item.notes)}
      </td>
      <td style="padding: 10px 12px; font-size: 13px; color: #4b5563;">${escapeHtml(label)}</td>
      <td style="padding: 10px 12px; font-size: 14px; text-align: right; color: #1f2937;">$${fin.netFormatted}</td>
      <td style="padding: 10px 12px; font-size: 14px; text-align: right; color: #1f2937;">$${fin.gstFormatted}</td>
      <td style="padding: 10px 12px; font-size: 14px; text-align: right; font-weight: 600; color: #111827;">$${fin.totalFormatted}</td>
    </tr>`;
  }).join('');

  let unmatchedRow = '';
  if (hasUnmatched) {
    const uFormatted = (unmatchedCents! / 100).toFixed(2);
    totalNetCents += unmatchedCents!;
    totalCentsSum += unmatchedCents!;
    unmatchedRow = `<tr style="border-bottom: 1px solid #e5e7eb;">
      <td style="padding: 10px 12px; font-size: 14px; color: #1f2937;">
        <strong>Stripe Fees / Adjustments</strong>
        <div style="font-size: 12px; color: #6b7280;">Processing fees, adjustments or unlinked items</div>
      </td>
      <td style="padding: 10px 12px; font-size: 13px; color: #4b5563;">Adjustment</td>
      <td style="padding: 10px 12px; font-size: 14px; text-align: right; color: #1f2937;">$${uFormatted}</td>
      <td style="padding: 10px 12px; font-size: 14px; text-align: right; color: #1f2937;">$0.00</td>
      <td style="padding: 10px 12px; font-size: 14px; text-align: right; font-weight: 600; color: #111827;">$${uFormatted}</td>
    </tr>`;
  }

  const netFormatted = (totalNetCents / 100).toFixed(2);
  const gstFormatted = (totalGstCents / 100).toFixed(2);
  const totalFormatted = (totalCentsSum / 100).toFixed(2);

  return `<table style="width: 100%; border-collapse: collapse; margin: 20px 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
    <thead>
      <tr style="background-color: #f3f4f6; border-bottom: 2px solid #d1d5db;">
        <th style="padding: 10px 12px; text-align: left; font-size: 12px; text-transform: uppercase; color: #4b5563;">Job Description</th>
        <th style="padding: 10px 12px; text-align: left; font-size: 12px; text-transform: uppercase; color: #4b5563;">Type</th>
        <th style="padding: 10px 12px; text-align: right; font-size: 12px; text-transform: uppercase; color: #4b5563;">Ex GST</th>
        <th style="padding: 10px 12px; text-align: right; font-size: 12px; text-transform: uppercase; color: #4b5563;">GST</th>
        <th style="padding: 10px 12px; text-align: right; font-size: 12px; text-transform: uppercase; color: #4b5563;">Total</th>
      </tr>
    </thead>
    <tbody>
      ${rows}
      ${unmatchedRow}
      <tr style="background-color: #f9fafb; font-weight: bold; border-top: 2px solid #374151;">
        <td colspan="2" style="padding: 12px; font-size: 14px; color: #111827;">Total Deposit Amount:</td>
        <td style="padding: 12px; font-size: 14px; text-align: right; color: #111827;">$${netFormatted}</td>
        <td style="padding: 12px; font-size: 14px; text-align: right; color: #111827;">$${gstFormatted}</td>
        <td style="padding: 12px; font-size: 15px; text-align: right; color: #00875a;">$${totalFormatted}</td>
      </tr>
    </tbody>
  </table>`;
}

/**
 * Generates plain-text breakdown for transactional payout notification emails
 */
export function generateRemittanceEmailText(items: ConsolidatedRemittanceItem[], unmatchedCents?: number): string {
  const hasItems = items && items.length > 0;
  const hasUnmatched = typeof unmatchedCents === 'number' && unmatchedCents !== 0;
  if (!hasItems && !hasUnmatched) {
    return 'No itemized breakdown available for this payout.';
  }

  let grandTotal = 0;
  const lines = (items || []).map((item) => {
    const fin = calculateRemittanceFinancials(item.amountCents, item.gstAmountCents, item.transactionType, item.tradeCompany, item.asOf, item.amountConvention);
    grandTotal += fin.totalCents;
    const type = formatTransactionTypeLabel(item.transactionType);
    const ref = item.jobId ? item.jobId.replace(/-/g, '').substring(0, 8).toUpperCase() : '';
    const line = fin.gstCents > 0
      ? `- ${item.jobTitle || 'Job Services'} (${ref ? 'Ref: ' + ref + ', ' : ''}${type}): $${fin.totalFormatted} (ex-GST: $${fin.netFormatted}, GST: $${fin.gstFormatted})`
      : `- ${item.jobTitle || 'Job Services'} (${ref ? 'Ref: ' + ref + ', ' : ''}${type}): $${fin.totalFormatted}`;
    return [line, ...cleanNotes(item.notes).map((n) => `  ${n}`)].join('\n');
  });

  if (hasUnmatched) {
    grandTotal += unmatchedCents!;
    lines.push(`- Stripe Fees / Adjustments: $${(unmatchedCents! / 100).toFixed(2)}`);
  }

  lines.push(`\nTotal Deposit: $${(grandTotal / 100).toFixed(2)}`);
  return lines.join('\n');
}

